import { MessageDescriptor } from 'react-intl';
import { socialBlockMessages as messages } from './socialBlockMessages';
import { SocialPostType, SocialSummaryChannel, SocialSummaryPost } from './socialSummaryTypes';

/**
 * What a channel row shows in place of its numbers (design d2).
 *
 * - `reconnect`: the channel needs a new sign-in. The row shows "Reconnect".
 * - `reading`: the first daily read did not finish. The row shows "Reading your numbers".
 * - `numbers`: the follower number and its change. When a read finished and the platform gave
 *   no follower count, the row shows a dash and no change pill.
 */
export type SocialRowState = 'reconnect' | 'reading' | 'numbers';

/** Reconnect comes first: a channel that needs a new sign-in can still have old numbers. */
export function socialRowState(channel: SocialSummaryChannel): SocialRowState {
  if (channel.needsReconnect) {
    return 'reconnect';
  }
  if (channel.followers !== null) {
    return 'numbers';
  }
  // No number and no finished read: the first read runs. No number after a read: the platform gives none.
  return channel.lastSyncedAt === null || channel.lastSyncedAt === undefined ? 'reading' : 'numbers';
}

/** YouTube counts subscribers. Every other platform counts followers. */
export function followersLabel(channel: SocialSummaryChannel): MessageDescriptor {
  return channel.platform === 'youtube' ? messages.subscribers : messages.followers;
}

export const postTypeMessages: Record<SocialPostType, MessageDescriptor> = {
  reel: messages.typeReel,
  short: messages.typeShort,
  video: messages.typeVideo,
  photo: messages.typePhoto,
  carousel: messages.typeCarousel,
  text: messages.typeText,
  story: messages.typeStory,
};

export interface BestPostNumber {
  key: 'views' | 'likes' | 'comments';
  message: MessageDescriptor;
  count: number;
}

/**
 * Views, likes and comments of the best post, in that order. A number that the platform does not
 * give (null) is left out: the block never shows it as 0.
 */
export function bestPostNumbers(post: SocialSummaryPost): BestPostNumber[] {
  const all: { key: BestPostNumber['key']; message: MessageDescriptor; count: number | null }[] = [
    { key: 'views', message: messages.bestPostViews, count: post.views },
    { key: 'likes', message: messages.bestPostLikes, count: post.likes },
    { key: 'comments', message: messages.bestPostComments, count: post.comments },
  ];
  return all.flatMap(({ key, message, count }) => (count === null ? [] : [{ key, message, count }]));
}
